// =============================================================================
// BUDGET PROGRESS - Barre de progression premium avec ventilation optionnelle
// =============================================================================

import { useMemo } from 'react';
import { Info, Wallet, AlertTriangle, CheckCircle2, PieChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BudgetData } from '@/hooks/useChartData';
import {
  formatMontantDizaine,
  formatPourcentage,
  CHART_STATUS_COLORS,
} from './chartConfig';
import { BudgetCategoryProgress } from './BudgetCategoryProgress';
import type { BudgetVsReel } from '@/types/budgetVentile.types';
import { cn } from '@/lib/utils';

interface Props {
  data: BudgetData;
  /** Données de ventilation (optionnel) */
  budgetVentile?: BudgetVsReel | null;
  /** Callback pour ouvrir le modal de ventilation */
  onConfigureVentile?: () => void;
  /** Faire clignoter le bouton */
  highlightButton?: boolean;
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function BudgetProgress({ data, budgetVentile, onConfigureVentile, highlightButton }: Props) {
  const { total, consomme, reste, pct, color, status } = data;
  const hasVentilation = budgetVentile?.has_ventilation ?? false;

  // Zones de la barre
  const zones = useMemo(() => [
    { end: 70, color: CHART_STATUS_COLORS.excellent, label: 'Confortable' },
    { end: 90, color: CHART_STATUS_COLORS.attention, label: 'À surveiller' },
    { end: 100, color: CHART_STATUS_COLORS.deficit, label: 'Critique' },
  ], []);

  // Icône selon le statut
  const StatusIcon = pct >= 90 ? AlertTriangle : pct >= 70 ? Info : CheckCircle2;
  const statusIconColor = pct >= 90 ? 'text-rose-400' : pct >= 70 ? 'text-amber-400' : 'text-emerald-400';

  if (total === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <Wallet className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-sm">Budget non défini</p>
        <p className="text-xs mt-1 opacity-70">Définissez un budget pour suivre la consommation</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col justify-center px-4 overflow-y-auto">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Consommation budget</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {formatMontantDizaine(consomme)} dépensés sur {formatMontantDizaine(total)}
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
          pct >= 90 ? 'bg-rose-500/10' : pct >= 70 ? 'bg-amber-500/10' : 'bg-emerald-500/10'
        }`}>
          <StatusIcon className={`h-4 w-4 ${statusIconColor}`} />
          <span className={`text-sm font-medium ${statusIconColor}`}>{status}</span>
        </div>
      </div>

      {/* Barre de progression globale */}
      <div className="relative mb-6">
        {/* Fond avec zones */}
        <div className="h-8 rounded-full overflow-hidden bg-card border border-border flex">
          {zones.map((zone, idx) => {
            const prevEnd = idx > 0 ? zones[idx - 1].end : 0;
            const width = zone.end - prevEnd;
            return (
              <div
                key={zone.label}
                className="h-full relative"
                style={{
                  width: `${width}%`,
                  backgroundColor: `${zone.color}15`,
                }}
              >
                {/* Ligne de séparation */}
                {idx < zones.length - 1 && (
                  <div className="absolute right-0 top-0 bottom-0 w-px bg-border/50" />
                )}
              </div>
            );
          })}
        </div>

        {/* Barre de progression remplie */}
        <div
          className="absolute top-0 left-0 h-8 rounded-full transition-all duration-1000 ease-out"
          style={{
            width: `${Math.min(pct, 100)}%`,
            background: `linear-gradient(90deg, ${CHART_STATUS_COLORS.excellent} 0%, ${color} 100%)`,
            boxShadow: `0 0 20px ${color}40`,
          }}
        />

        {/* Indicateur de position */}
        <div
          className="absolute top-0 transition-all duration-1000 ease-out flex flex-col items-center"
          style={{
            left: `${Math.min(pct, 100)}%`,
            transform: 'translateX(-50%)',
          }}
        >
          {/* Valeur au-dessus */}
          <div
            className="px-2.5 py-1 rounded-md text-xs font-bold text-white whitespace-nowrap -mt-8 mb-1"
            style={{ backgroundColor: color }}
          >
            {formatPourcentage(pct)}
          </div>
          {/* Losange indicateur */}
          <div
            className="w-3 h-3 rotate-45 -mt-1"
            style={{ backgroundColor: color }}
          />
        </div>

        {/* Labels des zones */}
        <div className="flex justify-between mt-2 px-1">
          {zones.map((zone, idx) => {
            const prevEnd = idx > 0 ? zones[idx - 1].end : 0;
            return (
              <div
                key={zone.label}
                className="text-xs text-muted-foreground"
                style={{ width: `${zone.end - prevEnd}%`, textAlign: 'center' }}
              >
                {zone.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Détails budget global */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Budget total</p>
          <p className="text-xl font-bold text-foreground">{formatMontantDizaine(total)}</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Réalisé</p>
          <p className="text-xl font-bold" style={{ color }}>{formatMontantDizaine(consomme)}</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Disponible</p>
          <p className={`text-xl font-bold ${reste >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {formatMontantDizaine(reste)}
          </p>
        </div>
      </div>

      {/* Section ventilation — fond distinct pour séparation visuelle */}
      {hasVentilation && budgetVentile?.categories ? (
        <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/15 border-l-2 border-l-primary/50 border-t-2 border-t-blue-500/40">
          <BudgetCategoryProgress
            categories={budgetVentile.categories}
            onEdit={onConfigureVentile || (() => {})}
          />
        </div>
      ) : onConfigureVentile ? (
        <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/15 border-l-2 border-l-primary/50 border-t-2 border-t-blue-500/40">
          <div className="flex items-start gap-3">
            <PieChart className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-sm text-foreground">
                Définir un budget par poste de dépense
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Attribuez un montant maximum à chaque catégorie (main d'oeuvre, matériaux,
                sous-traitance, frais annexes). Vous serez alerté si un poste dépasse
                son enveloppe, même si le budget global est OK.
              </p>
              <Button
                size="sm"
                className={cn(
                  "mt-3 gap-2 bg-yellow-500 hover:bg-yellow-600 text-black border-yellow-500 font-semibold",
                  highlightButton && "animate-highlight-pulse"
                )}
                onClick={onConfigureVentile}
              >
                <PieChart className="h-3.5 w-3.5" />
                Répartir mon budget
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Info bulle (masquée si ventilation active pour économiser de la place) */}
      {!hasVentilation && (
        <div className="mt-6 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
          <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            {reste > 0
              ? `Il vous reste ${formatMontantDizaine(reste)} sur le budget initial. ${
                  pct < 70 ? 'Vous êtes dans une zone confortable.' :
                  pct < 90 ? 'Surveillez vos dépenses pour rester dans les clous.' :
                  'Attention, le budget est presque épuisé !'
                }`
              : `Le budget est dépassé de ${formatMontantDizaine(Math.abs(reste))}. Action corrective recommandée.`
            }
          </p>
        </div>
      )}
    </div>
  );
}
